<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import Paginator from "@/components/modules/EventJournal/Paginator.vue";
import type {Paginator as PaginatorType} from "@/types/paginator";
import IconStatusAlert from "@/assets/icons/IconStatusAlert.vue";
import IconStatusInfo from "@/assets/icons/IconStatusInfo.vue";
import IconStatusError from "@/assets/icons/IconStatusError.vue";
import TableLoader from "@/components/ui/loaders/TableLoader.vue";
import TableEmptyData from "@/components/ui/loaders/TableEmptyData.vue";
import {EventLogItem} from "@/types/events";

interface MainTableProps {
  items: EventLogItem[];
  paginator: PaginatorType;
  loading: boolean;
}

interface MainTableEmits {
  (_e: "pageChange", _page: number): void;
}

const props = defineProps<MainTableProps>();
const emit = defineEmits<MainTableEmits>();

// Обработчик перехода на страницу
const handleGoToPage = (page: number) => {
  emit("pageChange", page);
  // Сбрасываем позицию скролла на начало при смене страницы
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = 0;
  }
};

// Конфигурация колонок: какие поля отображать и их заголовки
const columns = [
  { key: "timestamp", label: "Дата и время" },
  { key: "message", label: "Событие" },
  { key: "user_id", label: "Пользователь" }
];

// Реф на контейнер таблицы
const scrollContainer = ref<HTMLElement | null>(null);

// Флаг. Наличие скролла у контейнера таблицы
const hasScroll = ref(false);

// Объект скроллбара
let ps: PerfectScrollbar | null = null;
let resizeObserverInstance: ResizeObserver | null = null;

// Форматирование даты и времени
const formatDateTime = (timestamp: string) => {
  const utcTimestamp = timestamp.endsWith("Z") ? timestamp : timestamp + "Z";
  const date = new Date(utcTimestamp);

  return date.toLocaleString("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

// Форматирование значения ячейки в зависимости от типа
const formatCellValue = (row: EventLogItem, columnKey: string) => {
  const value = row[columnKey as keyof EventLogItem];

  if (columnKey === "timestamp" && typeof value === "string") {
    return formatDateTime(value);
  }

  return value ?? "-";
};

// Проверка наличия скролла у контейнера таблицы.
const checkScroll = () => {
  if (scrollContainer.value) {
    hasScroll.value = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
  }
};

onMounted(() => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });

    checkScroll();

    // Наблюдаем за изменениями размера контейнера
    resizeObserverInstance = new ResizeObserver(() => {
      checkScroll();
      ps?.update();
    });
    resizeObserverInstance.observe(scrollContainer.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserverInstance) {
    resizeObserverInstance.disconnect();
    resizeObserverInstance = null;
  }
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

</script>

<template>
  <div class="wrapper">
    <div class="main-table">
      <!--   Шапка таблицы   -->
      <div
        :class="{ 'main-table__header--with-scroll': hasScroll }"
        class="main-table__header main-table__row main-table__row--header"
      >
        <div
          v-for="column in columns"
          :key="column.key"
          class="main-table__col main-table__col--header"
        >
          {{ column.label }}
        </div>
      </div>
      <!--   Основное содержимое таблицы    -->
      <div
        ref="scrollContainer"
        class="main-table__body"
      >
        <div
          v-for="row in props.items"
          :key="row.id"
          class="main-table__row"
          :class="{ 'main-table__row--with-scroll': hasScroll }"
        >
          <div
            v-for="column in columns"
            :key="column.key"
            class="main-table__col"
          >
            <template v-if="column.key === 'message'">
              <div class="main-table__status">
                <template v-if="!row.validation_status">
                  <IconStatusAlert />
                </template>
                <template v-if="row.validation_status === 'validated'">
                  <IconStatusInfo />
                </template>
                <template v-if="row.validation_status === 'pre_validation_error'">
                  <IconStatusError />
                </template>
              </div>
            </template>
            <div class="main-table__message">
              {{ formatCellValue(row, column.key) }}
            </div>
          </div>
        </div>
        <div class="main-table__body--separator" />
        <TableEmptyData
          v-if="!loading && props.items.length === 0"
          subtitle="Данные по указанным параметрам не найдены"
        />
        <TableLoader
          v-if="loading"
          position="absolute"
        />
      </div>
      <!--   Пагинатор    -->
      <div class="main-table__footer">
        <Paginator
          :paginator="props.paginator"
          @go-to-page="handleGoToPage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/style/perfect-scrollbar";

.wrapper {
  background-color: var(--background-01);
  padding: 10px;
  border-radius: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.main-table {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;

  &__status {
    min-width: 24px;
    min-height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__footer {
    display: flex;
    justify-content: center;
    margin-top: 10px;
  }

  &__header {
    flex-shrink: 0;

    &--with-scroll {
      margin-right: 28px;
    }
  }

  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
    background-color: var(--background-bg-white);
  }

  &__footer {
    flex-shrink: 0;
  }

  &__row {
    display: grid;
    grid-template-columns: 180px 2fr 1fr;
    flex-shrink: 0;

    &:not(&--header):not(:last-child) {
      border-bottom: 1px solid var(--purple-purple20);
    }

    // Отступ для ползунка скроллбара.
    &--with-scroll {
      margin-right: 28px;
    }

    &--header {
      position: sticky;
      top: 0;
      z-index: 1;
    }
  }

  &__message {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__col {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background-color: var(--background-bg-white);
    min-width: 0;


    &:first-child {
      text-align: center;
    }

    &:not(&--header):not(:last-child) {
      border-right: 1px solid var(--purple-purple20);

      // При отсутствии скролла последний элемент без border
      .main-table__row:not(.main-table__row--with-scroll) &:last-child {
        border-right: 0;
      }
    }

    &--header {
      font-weight: 500;
      font-size: 14px;
      line-height: 22px;
      color: var(--text-text-main);
      background-color: var(--background-01);

      &:first-child {
        justify-content: center;
      }
    }
  }
}
</style>